import PropTypes from 'prop-types';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  IconButton,
  makeStyles,
} from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import { TRANSACT_AS_PAYER, selector } from 'reducers/transactAs';
import { selector as busySelector } from 'selectors/busy';
import { selector as fspConfigPayALoanSelector } from 'selectors/fspConfigPayALoan';
import { logPalRoutingAudit } from 'util/api';
import ButtonWithCircularProgress from 'components/common/ButtonWithCircularProgress';
import EmbeddedSso from './EmbeddedSso';
import EnrolledPalAfterRegister from './EnrolledPalAfterRegister';

const useStyle = makeStyles((theme) => ({
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
}));

const shouldUseEnrolledPalAfterRegister = (ownerType, fspConfig) =>
  String(ownerType || '').toLowerCase() === 'enrolled' &&
  fspConfig?.ifpPalOnOff === 'on' &&
  fspConfig?.enrolledUserOffOn === 'on' &&
  !!(fspConfig?.guestUrl && String(fspConfig.guestUrl).trim());

const SsoContent = ({ payerName, onCancel }) => {
  const classes = useStyle();

  return (
    <>
      <DialogTitle>
        Transact As {payerName}
        <IconButton
          aria-label="Close Transact As Dialog"
          className={classes.closeButton}
          color="inherit"
          onClick={onCancel}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <EmbeddedSso exitSso={onCancel} />
    </>
  );
};

SsoContent.propTypes = {
  payerName: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
};

const ConfirmContent = ({ onCancel, onConfirm, payerName }) => {
  const [acceptedConfirm, setAcceptedConfirm] = useState(false);
  const isBusy = useSelector((state) => busySelector(state, TRANSACT_AS_PAYER));

  const toggleAcceptedConfirm = () => {
    setAcceptedConfirm(!acceptedConfirm);
  };

  const handleCancel = useCallback(() => {
    onCancel();
    setAcceptedConfirm(false);
  }, [onCancel, setAcceptedConfirm]);

  return (
    <>
      <DialogTitle>Confirm transact as payer</DialogTitle>
      <DialogContent>
        <FormControlLabel
          control={
            <Checkbox
              checked={acceptedConfirm}
              color="primary"
              onClick={toggleAcceptedConfirm}
            />
          }
          label={`By checking this box, you have confirmed with the user, ${payerName}, that he/she has authorized you to take this action on his/her behalf.`}
        />
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={handleCancel}>
          Cancel
        </Button>
        <ButtonWithCircularProgress
          busy={isBusy}
          color="primary"
          disabled={!acceptedConfirm}
          onClick={onConfirm}
        >
          Continue
        </ButtonWithCircularProgress>
      </DialogActions>
    </>
  );
};

ConfirmContent.propTypes = {
  payerName: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

ConfirmContent.defaultProps = {
  payerName: null,
};

const ConfirmDialog = ({
  payerName,
  open,
  onConfirm,
  onCancel,
  ownerType,
  fspId,
}) => {
  const transactAsData = useSelector(selector) || { success: false };
  const displaySso = transactAsData.success;
  const fspConfigPayALoan = useSelector((state) =>
    fspConfigPayALoanSelector(state, fspId)
  );
  const useEnrolledPal =
    displaySso &&
    shouldUseEnrolledPalAfterRegister(ownerType, fspConfigPayALoan) &&
    transactAsData.registerData;

  const routingAuditSent = useRef(false);
  useEffect(() => {
    if (!open || !displaySso) {
      routingAuditSent.current = false;
      return;
    }
    // Hold off if ownerType is 'enrolled' but fspConfigPayALoan hasn't loaded yet.
    // Firing before config is settled can produce a premature fallback audit that
    // then gets contradicted on the next render when config arrives and useEnrolledPal
    // flips to true.
    if (
      String(ownerType || '').toLowerCase() === 'enrolled' &&
      fspConfigPayALoan == null
    ) {
      return;
    }
    if (useEnrolledPal || routingAuditSent.current) return;
    routingAuditSent.current = true;
    const outcome =
      !ownerType || String(ownerType).trim() === ''
        ? 'unknown-fallback'
        : 'existing-route';
    const correlationId = `${Date.now()}-${Array.from(
      crypto.getRandomValues(new Uint8Array(5)),
      (b) => b.toString(16).padStart(2, '0')
    ).join('')}`;
    logPalRoutingAudit(fspId, { outcome, correlationId }).catch(
      () => undefined
    );
  }, [open, displaySso, useEnrolledPal, ownerType, fspId, fspConfigPayALoan]);

  let Content;
  if (!displaySso) {
    Content = ConfirmContent;
  } else if (useEnrolledPal) {
    Content = EnrolledPalAfterRegister;
  } else {
    Content = SsoContent;
  }

  return (
    <Dialog
      maxWidth={displaySso ? 'xl' : undefined}
      onClick={(event) => event.stopPropagation()}
      open={open}
    >
      <Content
        fspId={fspId}
        onCancel={onCancel}
        onConfirm={onConfirm}
        payerName={payerName}
      />
    </Dialog>
  );
};

ConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  payerName: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  ownerType: PropTypes.string,
  fspId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

ConfirmDialog.defaultProps = {
  payerName: null,
  ownerType: null,
  fspId: null,
};

export default ConfirmDialog;
